import { useState } from "react";
import { CheckCircle2, XCircle, ChevronDown, ChevronUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import type { SessionSummary, KidTabProps } from "./types";

type SessionsData = { sessions: SessionSummary[] };

interface Props extends KidTabProps<SessionsData> {}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

// Star rating display (1-3 scale)
function SkillPill({ rating }: { rating: number }) {
  const stars = "★".repeat(rating) + "☆".repeat(3 - rating);
  return (
    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-mono">
      {stars}
    </span>
  );
}

function NarrativeSlot({ narrative }: { narrative: Props["narrative"] }) {
  if (!narrative) return null;
  return (
    <div className="rounded-lg bg-teal-50 border border-teal-200 px-4 py-3 text-sm text-teal-800 mb-5">
      {narrative}
    </div>
  );
}

const NOTE_TRUNCATE = 80;

function SessionRow({ session }: { session: SessionSummary }) {
  const [expanded, setExpanded] = useState(false);
  const hasLongNote = session.coachNote && session.coachNote.length > NOTE_TRUNCATE;
  const noteText = session.coachNote
    ? expanded || !hasLongNote
      ? session.coachNote
      : session.coachNote.slice(0, NOTE_TRUNCATE) + "…"
    : null;

  return (
    <div
      className={`rounded-xl border px-4 py-3 ${
        session.attended
          ? "bg-white border-gray-100"
          : "bg-gray-50 border-gray-100 opacity-70"
      }`}
      data-testid={`session-row-${session.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        {/* Left: attendance icon + date + theme */}
        <div className="flex items-start gap-3 min-w-0">
          <div className="mt-0.5 shrink-0">
            {session.attended ? (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            ) : (
              <XCircle className="h-4 w-4 text-gray-300" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{session.theme}</p>
            <p className="text-xs text-gray-400 mt-0.5">{formatDate(session.date)}</p>
            {!session.attended && (
              <p className="text-xs text-gray-400 mt-1 italic">Did not attend</p>
            )}
          </div>
        </div>

        {/* Right: skill rating pill */}
        {session.attended && session.skillRating != null && (
          <div className="shrink-0">
            <SkillPill rating={session.skillRating} />
          </div>
        )}
      </div>

      {/* Coach note */}
      {noteText && (
        <div className="mt-2 ml-7">
          <p className="text-xs text-gray-500 italic">{noteText}</p>
          {hasLongNote && (
            <button
              onClick={() => setExpanded((e) => !e)}
              className="text-xs text-blue-500 mt-1 flex items-center gap-0.5 hover:underline"
            >
              {expanded ? (
                <><ChevronUp className="h-3 w-3" /> Show less</>
              ) : (
                <><ChevronDown className="h-3 w-3" /> Show more</>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function KidSessionsTab({ data, isLoading, isEmpty, error, onRetry, narrative }: Props) {
  if (isLoading) {
    return (
      <div className="space-y-3 py-4">
        <Skeleton className="h-6 w-48" />
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
        <AlertCircle className="h-8 w-8 text-red-400" />
        <p className="text-sm text-gray-500">{error}</p>
        {onRetry && <Button variant="outline" size="sm" onClick={onRetry}>Retry</Button>}
      </div>
    );
  }

  if (isEmpty || data.sessions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center text-gray-400">
        <p className="font-medium text-gray-500">No sessions logged yet</p>
        <p className="text-sm mt-1">Session history will appear here after the first batch entry.</p>
      </div>
    );
  }

  // Already newest-first from mock; make explicit
  const ordered = [...data.sessions].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return (
    <div className="space-y-3 py-2" data-testid="tab-content-sessions-loaded">
      <NarrativeSlot narrative={narrative} />
      <p className="text-xs text-gray-400 mb-1">
        {ordered.length} session{ordered.length !== 1 ? "s" : ""} — newest first
      </p>
      {ordered.map((session) => (
        <SessionRow key={session.id} session={session} />
      ))}
    </div>
  );
}
